import type { DecisionRow } from "../lib/types";

type Props = {
  decisions: DecisionRow[];
};

/**
 * Intraday-specific panel: groups today's decisions by setup_name so the
 * operator can see which TradingView-MCP setups the bot's been seeing.
 *
 * Observation-only mode (no ALPACA_KEY_INTRADAY) is the normal case
 * right now — the routine still detects setups but the entries are
 * denied with reason "no broker creds for intraday." The setup_name is
 * STILL on the decision row, so this panel surfaces those detections
 * even when no trade fired.
 *
 * Setup colors mirror the price-action / volume-action module naming:
 *   - volume_burst, climax_reversal, pocket_pivot → blue (volume)
 *   - breakout_with_volume, pin_bar_at_level,
 *     vwap_reclaim, engulfing_at_level         → cyan (price-action)
 *   - other / null                              → slate
 */

const SETUP_COLOR_MAP: Record<string, string> = {
  // price-action
  pin_bar_at_level: "bg-cyan-900/40 text-cyan-300",
  breakout_with_volume: "bg-cyan-900/40 text-cyan-300",
  vwap_reclaim: "bg-cyan-900/40 text-cyan-300",
  engulfing_at_level: "bg-cyan-900/40 text-cyan-300",
  // volume-action
  volume_burst: "bg-sky-900/50 text-sky-200",
  climax_reversal: "bg-sky-900/50 text-sky-200",
  pocket_pivot: "bg-sky-900/50 text-sky-200",
};

function setupColor(name: string): string {
  return SETUP_COLOR_MAP[name] ?? "bg-slate-800 text-slate-300";
}

export function IntradaySetupsPanel({ decisions }: Props) {
  // Filter to rows with a setup_name (denied or not). The intraday
  // routine populates setup_name even when the order is rejected.
  const withSetup = decisions.filter((d) => d.setup_name);

  if (withSetup.length === 0) {
    return (
      <section className="rounded-lg border border-ink-700 bg-ink-900">
        <div className="border-b border-ink-700 px-3 py-2 text-sm font-semibold text-slate-200">
          Intraday setups today
        </div>
        <div className="px-3 py-6 text-center text-sm text-slate-500">
          No setups detected today.
          <div className="mt-1 text-xs text-slate-600">
            The intraday-monitor routine fires every 15 min during RTH and
            reads SPY/QQQ/AAPL/MSFT/NVDA via TradingView MCP. Setups appear
            here when the bot sees one.
          </div>
        </div>
      </section>
    );
  }

  // Aggregate counts by setup_name (allowed vs denied).
  type AggRow = { name: string; allowed: number; denied: number };
  const agg = new Map<string, AggRow>();
  for (const d of withSetup) {
    const key = d.setup_name as string;
    const row = agg.get(key) ?? { name: key, allowed: 0, denied: 0 };
    if (d.allowed === 1) row.allowed += 1;
    else row.denied += 1;
    agg.set(key, row);
  }
  const aggRows = Array.from(agg.values()).sort(
    (a, b) => b.allowed + b.denied - (a.allowed + a.denied),
  );

  // Symbol breakdown — which tickers fired today?
  const bySymbol = new Map<string, number>();
  for (const d of withSetup) {
    bySymbol.set(d.symbol, (bySymbol.get(d.symbol) ?? 0) + 1);
  }
  const symbolEntries = Array.from(bySymbol.entries()).sort(
    (a, b) => b[1] - a[1],
  );

  return (
    <section className="rounded-lg border border-ink-700 bg-ink-900">
      <div className="border-b border-ink-700 px-3 py-2 text-sm">
        <span className="font-semibold text-slate-200">
          Intraday setups today
        </span>
        <span className="ml-2 text-xs font-normal text-slate-500">
          ({withSetup.length} detections — via TradingView MCP)
        </span>
      </div>

      {/* Setup-name breakdown */}
      <div className="border-b border-ink-700 px-3 py-3">
        <div className="mb-2 text-xs uppercase tracking-wider text-slate-500">
          By setup
        </div>
        <div className="flex flex-wrap gap-2">
          {aggRows.map((r) => (
            <div
              key={r.name}
              className={`rounded px-2 py-1 text-xs ${setupColor(r.name)}`}
            >
              <span className="font-mono">{r.name}</span>
              <span className="ml-1.5 text-[10px] opacity-80">
                {r.allowed + r.denied}
                {r.allowed > 0 && (
                  <span className="ml-1 text-emerald-300">
                    ({r.allowed} traded)
                  </span>
                )}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Symbol breakdown */}
      <div className="border-b border-ink-700 px-3 py-3">
        <div className="mb-2 text-xs uppercase tracking-wider text-slate-500">
          By symbol
        </div>
        <div className="flex flex-wrap gap-2 font-mono text-xs">
          {symbolEntries.map(([sym, n]) => (
            <span
              key={sym}
              className="rounded bg-ink-800 px-2 py-1 text-slate-300"
            >
              {sym} <span className="ml-1 text-slate-500">×{n}</span>
            </span>
          ))}
        </div>
      </div>

      {/* Latest 10 detections — chronologically newest first */}
      <div className="px-3 py-3">
        <div className="mb-2 text-xs uppercase tracking-wider text-slate-500">
          Recent detections
        </div>
        <ul className="space-y-1.5 text-xs">
          {withSetup.slice(0, 10).map((d) => (
            <li
              key={d.id}
              className="flex flex-wrap items-baseline justify-between gap-2"
            >
              <div className="flex items-baseline gap-2">
                <span className="font-mono text-slate-500">
                  {d.timestamp_utc.slice(11, 16)}Z
                </span>
                <span className="font-mono text-slate-100">{d.symbol}</span>
                <span
                  className={`rounded px-1.5 py-0.5 text-[10px] ${setupColor(d.setup_name as string)}`}
                >
                  {d.setup_name}
                </span>
                <span
                  className={`text-[10px] font-medium uppercase ${
                    d.allowed === 1
                      ? "text-accent-ok"
                      : "text-slate-500"
                  }`}
                >
                  {d.allowed === 1 ? "traded" : "observed"}
                </span>
              </div>
            </li>
          ))}
        </ul>
        {withSetup.length > 10 && (
          <div className="mt-2 text-center text-[10px] text-slate-600">
            showing 10 most recent of {withSetup.length}
          </div>
        )}
      </div>
    </section>
  );
}
